import type { AppResourceBinding } from "@rally/agreed";
import { Col, Icon, openUrl, Tappable, Text, useTheme } from "@rally/ui-kit";
import { useCallback, useEffect, useRef, useState } from "react";
import { say } from "../../localization/say.ts";
import { cancelRequests, isLatestRequest, nextRequest } from "../../net/latest-request.ts";
import { read } from "../../net/rally.ts";
import type { Card } from "../panel/stacking.ts";
import { AppControls, type EditableApp } from "./app-controls.tsx";

export type ListedApp = {
  id: string;
  slug: string;
  name: string;
  description: string;
  placement: string;
  openMode: "card" | "embedded" | "focus" | "link";
  channel: string | null;
  display: Record<string, unknown>;
  config: Record<string, unknown>;
  context?: string;
  contextId?: string | null;
  conversation?: string;
  url: string | null;
  hostingMode: string | null;
  healthStatus: string | null;
};

type ReadApp = ListedApp &
  EditableApp & {
    editable: boolean;
    bindings: AppResourceBinding[];
    deployments: Array<{
      id?: string;
      url: string | null;
      hostingMode: string;
      healthStatus: string | null;
      embeddable: "yes" | "no" | "unknown";
      embeddabilityReason: string | null;
    }>;
    versions: Array<{ id: string; ordinal: number; sha256: string; createdAt: string }>;
    grants: Array<{ id: string; scope: string; scopeId: string | null }>;
  };

function theOneServedByEachAddress<T extends { url: string | null }>(held: readonly T[]): T[] {
  const seen = new Set<string>();
  const kept: T[] = [];
  for (const one of held) {
    const at = one.url ?? "";
    if (!at || seen.has(at)) continue;
    seen.add(at);
    kept.push(one);
  }
  return kept;
}

export function appCard(app: ListedApp): Card {
  return { key: `app:${app.id}`, title: app.name, render: () => <AppResource listed={app} /> };
}

function AppLink({ url, label, detail }: { url: string; label: string; detail: string }) {
  const theme = useTheme();
  return (
    <Tappable
      accessibilityRole="link"
      accessibilityLabel={`${say("apps.openExternal")}: ${label}`}
      onPress={() => void openUrl(url)}
      style={{
        minHeight: 44,
        flexDirection: "row",
        alignItems: "center",
        gap: theme.space[2],
        paddingHorizontal: theme.space[2],
        paddingVertical: theme.space[2],
        borderRadius: theme.radius.md,
      }}
    >
      <Icon name="activity" size={17} color="text.tertiary" />
      <Col style={{ flex: 1, minWidth: 0 }}>
        <Text typeName="body" color="text.primary" numberOfLines={1}>
          {label}
        </Text>
        <Text typeName="caption" color="text.tertiary" numberOfLines={1}>
          {detail}
        </Text>
      </Col>
      <Icon name="external-link" size={15} color="text.tertiary" />
    </Tappable>
  );
}

export function AppResource({ listed }: { listed: ListedApp }) {
  const theme = useTheme();
  const [app, setApp] = useState<ReadApp | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const request = useRef(0);
  const reload = useCallback(() => {
    const identity = nextRequest(request);
    setUnavailable(false);
    void read<ReadApp>("app.read", { app: listed.id, placement: listed.placement })
      .then((found) => {
        if (!isLatestRequest(request, identity)) return;
        setApp(found);
      })
      .catch(() => {
        if (!isLatestRequest(request, identity)) return;
        setApp(null);
        setUnavailable(true);
      });
  }, [listed.id, listed.placement]);
  useEffect(() => {
    setApp(null);
    reload();
    return () => cancelRequests(request);
  }, [reload]);
  if (unavailable) {
    return (
      <Col testID="app-panel-unavailable">
        <Text typeName="body" color="text.tertiary">
          {say("apps.unavailable")}
        </Text>
      </Col>
    );
  }
  if (!app) {
    return (
      <Col testID="app-panel-loading">
        <Text typeName="body" color="text.tertiary">
          {say("apps.loading")}
        </Text>
      </Col>
    );
  }
  const currentPlacement = app.placements.find((placement) => placement.id === listed.placement);
  const controlled = currentPlacement
    ? {
        ...app,
        placements: [currentPlacement, ...app.placements.filter((one) => one.id !== currentPlacement.id)],
      }
    : app;
  return (
    <Col style={{ gap: theme.space[4] }} testID="app-panel">
      {app.description ? (
        <Text typeName="body" color="text.secondary">
          {app.description}
        </Text>
      ) : null}

      {app.editable ? (
        <AppControls app={controlled} reload={reload} />
      ) : (
        <Text typeName="caption" color="text.tertiary">
          {say("apps.readOnlyPermissions")}
        </Text>
      )}

      <Col style={{ gap: theme.space[1] }} testID="app-deployments">
        <Text typeName="caption" color="text.tertiary" style={{ paddingHorizontal: theme.space[2] }}>
          {say("apps.deployments")}
        </Text>
        {app.deployments.length ? (
          theOneServedByEachAddress(app.deployments).map((deployment, index) =>
            deployment.url ? (
              <AppLink
                key={deployment.id ?? `${deployment.url}:${index}`}
                url={deployment.url}
                label={say("apps.production")}
                detail={
                  deployment.healthStatus === "healthy"
                    ? say("apps.healthy")
                    : deployment.healthStatus
                      ? say("apps.healthUnavailable")
                      : say("apps.healthPending")
                }
              />
            ) : null,
          )
        ) : (
          <Col
            style={{
              minHeight: 44,
              flexDirection: "row",
              alignItems: "center",
              gap: theme.space[2],
              paddingHorizontal: theme.space[2],
            }}
          >
            <Icon name="activity" size={17} color="text.tertiary" />
            <Text typeName="body" color="text.primary" style={{ flex: 1 }}>
              {say("apps.production")}
            </Text>
            <Text typeName="body" color="text.tertiary">
              {say("apps.awaitingDeployment")}
            </Text>
          </Col>
        )}
      </Col>

      {app.versions.length ? (
        <Col gap={1}>
          <Text typeName="caption" color="text.tertiary" style={{ paddingHorizontal: theme.space[2] }}>
            {say("apps.activity")}
          </Text>
          {app.versions.slice(0, 5).map((version) => (
            <Col
              key={version.id}
              style={{
                minHeight: 36,
                flexDirection: "row",
                alignItems: "center",
                gap: theme.space[2],
                paddingHorizontal: theme.space[2],
              }}
            >
              <Icon name="git-branch" size={16} color="text.tertiary" />
              <Text typeName="body" color="text.primary" style={{ flex: 1 }}>
                {`${say("apps.versionLabel")} ${version.ordinal}`}
              </Text>
            </Col>
          ))}
        </Col>
      ) : null}
    </Col>
  );
}
